import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  Users,
  Phone,
  Mail,
  Check,
  X,
  UserX,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  User,
  MapPin
} from 'lucide-react';
import { format, addDays, startOfWeek, addWeeks, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: { bg: 'bg-amber-500/20', text: 'text-amber-500', border: 'border-amber-500', label: 'Pendiente' },
  confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-500', border: 'border-blue-500', label: 'Confirmada' },
  seated: { bg: 'bg-green-500/20', text: 'text-green-500', border: 'border-green-500', label: 'Sentado' },
  completed: { bg: 'bg-gray-500/20', text: 'text-gray-500', border: 'border-gray-500', label: 'Completada' },
  cancelled: { bg: 'bg-red-500/20', text: 'text-red-500', border: 'border-red-500', label: 'Cancelada' },
  no_show: { bg: 'bg-red-800/20', text: 'text-red-800', border: 'border-red-800', label: 'No vino' },
};

const TIME_SLOTS = [];
for (let h = 8; h < 24; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:30`);
}

export default function Reservations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    unit_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    party_size: 2,
    reservation_date: format(new Date(), 'yyyy-MM-dd'),
    reservation_time: '12:00',
    duration_minutes: 120,
    notes: ''
  });

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate, weekStart, viewMode]);

  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API}/pos/dashboard`);
      const allUnits = response.data.spaces?.flatMap(s => s.units) || [];
      setUnits(allUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      let start, end;
      if (viewMode === 'day') {
        start = format(selectedDate, 'yyyy-MM-dd');
        end = start;
      } else {
        start = format(weekStart, 'yyyy-MM-dd');
        end = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      }
      
      const response = await axios.get(`${API}/reservations/calendar?start_date=${start}&end_date=${end}`);
      setReservations(Object.values(response.data.calendar || {}).flat());
      if (response.data.units?.length > 0 && units.length === 0) {
        setUnits(response.data.units);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      unit_id: units[0]?.unit_id || '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      party_size: 2,
      reservation_date: format(selectedDate, 'yyyy-MM-dd'),
      reservation_time: '12:00',
      duration_minutes: 120,
      notes: ''
    });
    setEditingReservation(null);
  };

  const openCreateDialog = (date = selectedDate, time = null) => {
    resetForm();
    setForm(prev => ({
      ...prev,
      unit_id: units[0]?.unit_id || '',
      reservation_date: format(date, 'yyyy-MM-dd'),
      reservation_time: time || '12:00'
    }));
    setShowDialog(true);
  };

  const openEditDialog = (reservation) => {
    setEditingReservation(reservation);
    setForm({
      unit_id: reservation.unit_id,
      customer_name: reservation.customer_name,
      customer_phone: reservation.customer_phone || '',
      customer_email: reservation.customer_email || '',
      party_size: reservation.party_size,
      reservation_date: reservation.reservation_date,
      reservation_time: reservation.reservation_time,
      duration_minutes: reservation.duration_minutes,
      notes: reservation.notes || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.customer_name || !form.unit_id) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      if (editingReservation) {
        await axios.put(`${API}/reservations/${editingReservation.reservation_id}`, form);
        toast.success('Reserva actualizada');
      } else {
        await axios.post(`${API}/reservations`, form);
        toast.success('Reserva creada');
      }
      
      setShowDialog(false);
      resetForm();
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar reserva');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (reservationId, action) => {
    try {
      await axios.post(`${API}/reservations/${reservationId}/${action}`);
      toast.success('Estado actualizado');
      fetchReservations();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const cancelReservation = async (reservationId) => {
    try {
      await axios.put(`${API}/reservations/${reservationId}`, { status: 'cancelled' });
      toast.success('Reserva cancelada');
      fetchReservations();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const deleteReservation = async (reservationId) => {
    if (!window.confirm('¿Eliminar esta reserva?')) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}`);
      toast.success('Reserva eliminada');
      fetchReservations();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getReservationsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.filter(r => r.reservation_date === dateStr);
  };

  const getReservationsForSlot = (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.filter(r => {
      if (r.reservation_date !== dateStr) return false;
      const resStart = parseInt(r.reservation_time.replace(':', ''));
      const resEnd = parseInt(r.end_time?.replace(':', '') || '0');
      const slotTime = parseInt(time.replace(':', ''));
      return slotTime >= resStart && slotTime < resEnd;
    });
  };

  const navigatePrev = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, -1));
    } else {
      setWeekStart(addWeeks(weekStart, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setWeekStart(addWeeks(weekStart, 1));
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Reservas</h1>
              <p className="text-sm text-muted-foreground">Gestiona las reservas de mesas</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode */}
            <div className="flex bg-muted rounded-lg p-1">
              <Button 
                variant={viewMode === 'day' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('day')}
              >
                Día
              </Button>
              <Button 
                variant={viewMode === 'week' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('week')}
              >
                Semana
              </Button>
            </div>

            <Button variant="outline" size="icon" onClick={fetchReservations}>
              <RefreshCw className="w-4 h-4" />
            </Button>

            <Button onClick={() => openCreateDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Reserva
            </Button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={navigatePrev}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={navigateNext}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={goToToday}>
              Hoy
            </Button>
          </div>

          <h2 className="text-lg font-semibold">
            {viewMode === 'day' 
              ? format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: es })
              : `${format(weekStart, "d MMM", { locale: es })} - ${format(addDays(weekStart, 6), "d MMM yyyy", { locale: es })}`
            }
          </h2>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <CalendarIcon className="w-4 h-4 mr-2" />
                Ir a fecha
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setWeekStart(startOfWeek(date, { weekStartsOn: 1 }));
                  }
                }}
                locale={es}
              />
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* Content */}
      <main className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : viewMode === 'day' ? (
          /* Day View */
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Timeline */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    {getReservationsForDate(selectedDate).length} reservas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-1">
                      {TIME_SLOTS.filter((_, i) => i % 2 === 0).map((time) => {
                        const slotReservations = getReservationsForSlot(selectedDate, time);
                        
                        return (
                          <div 
                            key={time} 
                            className="flex border-b border-border/50 min-h-[60px] hover:bg-muted/30 cursor-pointer"
                            onClick={() => slotReservations.length === 0 && openCreateDialog(selectedDate, time)}
                          >
                            <div className="w-16 py-2 text-sm text-muted-foreground font-mono">
                              {time}
                            </div>
                            <div className="flex-1 py-1 px-2 space-y-1">
                              {slotReservations.filter((r, i, arr) => 
                                arr.findIndex(x => x.reservation_id === r.reservation_id) === i
                              ).map((res) => (
                                <div
                                  key={res.reservation_id}
                                  className={`p-2 rounded-lg border ${STATUS_COLORS[res.status]?.bg} ${STATUS_COLORS[res.status]?.border} cursor-pointer`}
                                  onClick={(e) => { e.stopPropagation(); openEditDialog(res); }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{res.customer_name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {res.unit_name}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Clock className="w-3 h-3" />
                                      {res.reservation_time} - {res.end_time}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {res.party_size}
                                    </span>
                                    <Badge className={`${STATUS_COLORS[res.status]?.bg} ${STATUS_COLORS[res.status]?.text}`}>
                                      {STATUS_COLORS[res.status]?.label}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Today's Reservations List */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Lista del día</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[550px]">
                    <div className="space-y-3">
                      {getReservationsForDate(selectedDate)
                        .sort((a, b) => a.reservation_time.localeCompare(b.reservation_time))
                        .map((res) => (
                        <Card 
                          key={res.reservation_id} 
                          className={`cursor-pointer hover:shadow-md transition-shadow ${STATUS_COLORS[res.status]?.border} border-l-4`}
                          onClick={() => openEditDialog(res)}
                        >
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold">{res.customer_name}</p>
                                <p className="text-sm text-muted-foreground">{res.unit_name}</p>
                              </div>
                              <Badge className={`${STATUS_COLORS[res.status]?.bg} ${STATUS_COLORS[res.status]?.text}`}>
                                {STATUS_COLORS[res.status]?.label}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {res.reservation_time}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {res.party_size}
                              </span>
                            </div>

                            {/* Quick Actions */}
                            {res.status === 'pending' && (
                              <div className="flex gap-2 mt-3">
                                <Button size="sm" className="flex-1 h-8" onClick={(e) => { e.stopPropagation(); updateStatus(res.reservation_id, 'confirm'); }}>
                                  <Check className="w-3 h-3 mr-1" />
                                  Confirmar
                                </Button>
                                <Button size="sm" variant="destructive" className="h-8" onClick={(e) => { e.stopPropagation(); cancelReservation(res.reservation_id); }}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                            {res.status === 'confirmed' && (
                              <div className="flex gap-2 mt-3">
                                <Button size="sm" className="flex-1 h-8 bg-green-600 hover:bg-green-700" onClick={(e) => { e.stopPropagation(); updateStatus(res.reservation_id, 'seat'); }}>
                                  <User className="w-3 h-3 mr-1" />
                                  Llegó
                                </Button>
                                <Button size="sm" variant="outline" className="h-8" onClick={(e) => { e.stopPropagation(); updateStatus(res.reservation_id, 'no-show'); }}>
                                  <UserX className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                      
                      {getReservationsForDate(selectedDate).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>Sin reservas para este día</p>
                          <Button className="mt-4" onClick={() => openCreateDialog()}>
                            <Plus className="w-4 h-4 mr-2" />
                            Agregar
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Week View */
          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-8 border-b">
                <div className="p-2 text-center text-sm font-medium text-muted-foreground border-r" />
                {Array.from({ length: 7 }).map((_, i) => {
                  const date = addDays(weekStart, i);
                  const isToday = isSameDay(date, new Date());
                  const dayReservations = getReservationsForDate(date);
                  
                  return (
                    <div 
                      key={i} 
                      className={`p-2 text-center border-r ${isToday ? 'bg-primary/10' : ''}`}
                    >
                      <p className="text-xs text-muted-foreground">
                        {format(date, 'EEE', { locale: es })}
                      </p>
                      <p className={`text-lg font-semibold ${isToday ? 'text-primary' : ''}`}>
                        {format(date, 'd')}
                      </p>
                      <Badge variant="secondary" className="text-xs">
                        {dayReservations.length}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              
              <ScrollArea className="h-[500px]">
                {TIME_SLOTS.filter((_, i) => i % 2 === 0).map((time) => (
                  <div key={time} className="grid grid-cols-8 border-b min-h-[50px]">
                    <div className="p-1 text-xs text-muted-foreground border-r flex items-center justify-center font-mono">
                      {time}
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => {
                      const date = addDays(weekStart, i);
                      const slotRes = getReservationsForSlot(date, time);
                      
                      return (
                        <div 
                          key={i} 
                          className="border-r p-1 hover:bg-muted/30 cursor-pointer"
                          onClick={() => slotRes.length === 0 && openCreateDialog(date, time)}
                        >
                          {slotRes.filter((r, idx, arr) => 
                            arr.findIndex(x => x.reservation_id === r.reservation_id) === idx
                          ).slice(0, 2).map((res) => (
                            <div
                              key={res.reservation_id}
                              className={`text-xs p-1 rounded mb-1 truncate ${STATUS_COLORS[res.status]?.bg} ${STATUS_COLORS[res.status]?.text} cursor-pointer`}
                              onClick={(e) => { e.stopPropagation(); openEditDialog(res); }}
                              title={`${res.customer_name} - ${res.unit_name}`}
                            >
                              {res.customer_name}
                            </div>
                          ))}
                          {slotRes.length > 2 && (
                            <span className="text-xs text-muted-foreground">+{slotRes.length - 2}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReservation ? 'Editar Reserva' : 'Nueva Reserva'}
            </DialogTitle>
            <DialogDescription>
              {editingReservation 
                ? `Reserva de ${editingReservation.customer_name}`
                : 'Completa los datos de la reserva'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={form.reservation_date}
                  onChange={(e) => setForm({ ...form, reservation_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora *</Label>
                <Select value={form.reservation_time} onValueChange={(v) => setForm({ ...form, reservation_time: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card max-h-60">
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mesa/Unidad *</Label>
                <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {units.map((unit) => (
                      <SelectItem key={unit.unit_id} value={unit.unit_id}>
                        {unit.name} ({unit.capacity} pers.)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duración</Label>
                <Select value={form.duration_minutes.toString()} onValueChange={(v) => setForm({ ...form, duration_minutes: parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="60">1 hora</SelectItem>
                    <SelectItem value="90">1.5 horas</SelectItem>
                    <SelectItem value="120">2 horas</SelectItem>
                    <SelectItem value="180">3 horas</SelectItem>
                    <SelectItem value="240">4 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nombre del cliente *</Label>
              <Input
                placeholder="Juan Pérez"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  placeholder="+54 11 1234-5678"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Personas</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.party_size}
                  onChange={(e) => setForm({ ...form, party_size: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="cliente@email.com"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                placeholder="Observaciones, pedidos especiales..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            {/* Status Actions for editing */}
            {editingReservation && (
              <div className="pt-4 border-t">
                <Label className="mb-2 block">Estado actual: <Badge className={STATUS_COLORS[editingReservation.status]?.bg}>{STATUS_COLORS[editingReservation.status]?.label}</Badge></Label>
                <div className="flex flex-wrap gap-2">
                  {editingReservation.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => { updateStatus(editingReservation.reservation_id, 'confirm'); setShowDialog(false); }}>
                        <Check className="w-4 h-4 mr-1" /> Confirmar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => { cancelReservation(editingReservation.reservation_id); setShowDialog(false); }}>
                        <X className="w-4 h-4 mr-1" /> Cancelar
                      </Button>
                    </>
                  )}
                  {editingReservation.status === 'confirmed' && (
                    <>
                      <Button size="sm" className="bg-green-600" onClick={() => { updateStatus(editingReservation.reservation_id, 'seat'); setShowDialog(false); }}>
                        <User className="w-4 h-4 mr-1" /> Llegó
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { updateStatus(editingReservation.reservation_id, 'no-show'); setShowDialog(false); }}>
                        <UserX className="w-4 h-4 mr-1" /> No vino
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { deleteReservation(editingReservation.reservation_id); setShowDialog(false); }}>
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingReservation ? 'Guardar cambios' : 'Crear reserva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
